declare global {
  interface Window {
    Intercom: any;
  }
}

// i18n
import { translate, translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import {
  useState,
  SyntheticEvent,
  useMemo,
  useContext,
  useEffect,
} from 'react';
import { useRouter } from 'next/router';

import { get, post, destroy, patch } from 'infras/rest_client';
import { UserProfileItem, ViewerItem } from 'presentations/api/schema/user';
import {
  EventTypeCreateResult,
  EventTypeListItem,
  EventTypeStatusUpdateBody,
  EventTypeUpdateBody,
} from 'presentations/api/schema/event_type';
import {
  hostEventTypeEditPath,
  apiPath,
  hostEventTypeNewPath,
  hostEventTypeSettingPath,
  dashboardCalendarsPath,
  dashboardPath,
  loginPath,
  webPath,
  eventTypePath,
} from 'presentations/routes/path';
import {
  SubscriptionChoosePlanBody,
  SubscriptionStateItem,
} from 'presentations/api/schema/subscription';
import {
  sortToViewerTopListItems,
  TeamUserItem,
  TeamUserListItem,
} from 'presentations/api/schema/team_user';
import { useRequest } from 'presentations/views/hooks/request';
import { EventTypeType } from 'domains/models/event_type/eventTypeType';
import { EventTypeOptionIndex } from 'presentations/views/components/organisms/HostEventTypeList';
import {
  DashboardPortalContext,
  setErrorMessage,
} from 'presentations/reducers/DashboardPortal';
import { Status } from 'domains/models/event_type/status';
import { Role } from 'domains/models/team_user/role';
import { TrialChannel } from 'domains/models/subscription/trial_channel';
import {
  DateTime,
  currentTimeZone,
  formatToYMD,
  getDateTime,
} from 'infras/datetime';
import { ProjectEventListItem } from 'presentations/api/schema/project/project_event';
import { IntegrationMergedEventListItem } from 'presentations/api/schema/calendar_event/integrationMerged';
import { openMergedEvent } from 'presentations/views/helpers/event';
import { initializeWebPush } from 'infras/firebase/client/webpush';
import { DateControllerActionType } from 'presentations/views/components/molecules/DateController';
import {
  isAndroid,
  isiOSApp as isiOSAppFunc,
} from 'presentations/views/helpers/electron';
import { UserListFilter } from 'presentations/views/components/organisms/UserListFilterNavigation';
import { EventTypeShareOptionIndex } from 'presentations/views/components/molecules/EventTypeShareMenu';
import FormExportFeature from 'domains/models/subscription/features/form_export';
import { useTaskList } from 'presentations/views/components/organisms/TaskList/hooks';
import {
  getPersistentItemWithViewer,
  infiniteCacheExpiration,
  setPersistentItemWithViewer,
} from 'presentations/views/helpers/storage';
import { TaskDetailListItem } from 'presentations/api/schema/task';
import { TaskStatus } from 'domains/models/task/status';
import { DisplayType } from 'domains/models/event_type/displayType';
import { setEvent } from 'presentations/views/helpers/analytics';
import EventTypeFeature from 'domains/models/subscription/features/event_type';

interface Props {
  viewer: ViewerItem;
}

const perPage = 20;

export function useEventTypeListPage(props: Props) {
  const t = translateInComponent(translations);

  const { dispatch, state: dashboardState } = useContext(
    DashboardPortalContext,
  );

  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [isEventIntegrating, setIsEventIntegrating] = useState<boolean>(false);
  const [
    embedEventType,
    setEmbedEventType,
  ] = useState<EventTypeListItem | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const userProfileRequest = useRequest(apiPath(`/v1/user/profile`));
  const userProfile: UserProfileItem | null = userProfileRequest.data || null;
  const timeZone = userProfile?.timeZone || currentTimeZone();
  const today = getDateTime(null, timeZone);
  const [currentDate, setCurrentDate] = useState<DateTime>(today);
  const [queryKeyword, setQueryKeyword] = useState<string>('');

  const defaultIsCompleteOnboard = useMemo(() => {
    return getPersistentItemWithViewer(
      'isCompleteEventTypeOnboard',
      props.viewer,
    );
  }, []);
  const [isCompleteOnboard, setIsCompleteOnboard] = useState<boolean>(
    defaultIsCompleteOnboard ?? true,
  );

  const defaultIsOpenOnboard = useMemo(() => {
    return getPersistentItemWithViewer('isOpenEventTypeOnboard', props.viewer);
  }, []);

  const [isOpenOnboard, setIsOpenOnboard] = useState<boolean>(
    defaultIsOpenOnboard ?? true,
  );
  const [
    deleteEventType,
    setDeleteEventType,
  ] = useState<EventTypeListItem | null>(null);
  const [copyEventType, setCopyEventType] = useState<EventTypeListItem | null>(
    null,
  );
  const [isShowingPlanUpgrade, setIsShowingPlanUpgrade] = useState<boolean>(
    false,
  );
  const [
    activateEventType,
    setActivateEventType,
  ] = useState<EventTypeListItem | null>(null);
  const [isShowingTrialEnd, setIsShowingTrialEnd] = useState<boolean>(false);
  const [isOpenCompleteDialog, setIsOpenCompleteDialog] = useState<boolean>(
    false,
  );
  const [isOpenTour, setIsOpenTour] = useState<boolean>(false);
  const [isReactivation, setIsReactivation] = useState<boolean>(false);

  const taskList = useTaskList({
    viewer: props.viewer,
    isDisabled: isCompleteOnboard,
  });
  const completeTaskCount = taskList.state.tasks.filter(
    (task: TaskDetailListItem) => task.status === TaskStatus.Done,
  ).length;
  const allTaskCount = taskList.state.tasks.length;

  useEffect(() => {
    if (
      defaultIsCompleteOnboard === null &&
      getDateTime(props.viewer.trialEndedAt) > getDateTime()
    ) {
      setIsCompleteOnboard(false);
      setPersistentItemWithViewer(
        'isCompleteEventTypeOnboard',
        false,
        infiniteCacheExpiration,
        props.viewer,
      );
    }
  }, [defaultIsCompleteOnboard, props.viewer.trialEndedAt]);

  useEffect(() => {
    if (taskList.state.isTaskLoading) return;
    if (!isCompleteOnboard && completeTaskCount === allTaskCount) {
      setIsCompleteOnboard(true);
      setPersistentItemWithViewer(
        'isCompleteEventTypeOnboard',
        true,
        infiniteCacheExpiration,
        props.viewer,
      );
    }
  }, [
    isCompleteOnboard,
    taskList.state.isTaskLoading,
    completeTaskCount,
    allTaskCount,
  ]);

  const [
    eventTypeForOnetimeLink,
    setEventTypeForOnetimeLink,
  ] = useState<EventTypeListItem | null>(null);

  const dateString = currentDate.toFormat(t('Specific day schedule'));
  const isToday = currentDate.hasSame(today, 'day');
  const isFuture = currentDate > today;

  const router = useRouter();
  const currentUserId = router.query.user
    ? String(router.query.user)
    : props.viewer.uid;
  const projectUid = router.query.project ? String(router.query.project) : null;
  const project = useMemo(() => {
    return (
      dashboardState.projects.find((project) => project.uid === projectUid) ||
      null
    );
  }, [projectUid, dashboardState.projects]);

  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isiOSApp = isiOSAppFunc(userAgent);

  const eventTypeFeature = new EventTypeFeature({
    planVersion: props.viewer.planVersion,
  });

  const startedAt = currentDate.startOf('day').toMillis();
  const endedAt = currentDate.plus({ day: 1 }).startOf('day').toMillis();
  const eventsRequest = useRequest(
    router.isReady && props.viewer.team
      ? apiPath(`/v1/teams/${props.viewer.team.uid}/users/schedules`, {
          started: startedAt,
          ended: endedAt,
          userId: [currentUserId],
          seminarFilter: 'grouping',
          includeReservedEvent: 1,
        })
      : null,
  );
  const events: ProjectEventListItem[] = eventsRequest.data || [];
  const isEventLoading = eventsRequest.isLoading;

  const eventTypesRequest = useRequest(
    apiPath(`/v1/teams/${props.viewer.team!.uid}/event_types`, {
      page: currentPage,
      perPage,
      userId: currentUserId,
      projectId: projectUid,
      sortType: 'recentUsed',
      eventTypeType: EventTypeType.Standard,
      keyword: queryKeyword || undefined,
    }),
  );
  const eventTypes: EventTypeListItem[] = eventTypesRequest.data || [];

  const teamUserRequest = useRequest(
    currentUserId === 'all' || projectUid
      ? null
      : apiPath(
          `/v1/teams/${props.viewer.team!.uid}/users/${
            currentUserId ?? props.viewer.uid
          }`,
        ),
  );
  const teamUser: TeamUserItem | null = teamUserRequest.data || null;

  const subscriptionStateRequest = useRequest(
    apiPath(`/v1/teams/${props.viewer.team!.uid}/subscriptions/state`),
  );
  const subscriptionState: SubscriptionStateItem | null =
    subscriptionStateRequest.data;
  let canAddEventType = false;
  if (subscriptionState) {
    canAddEventType = eventTypeFeature.canAdd(
      subscriptionState.planId,
      subscriptionState.viewerEnabledEventTypeCount,
    );
  }

  const currentTeamUser = useMemo(() => {
    return teamUser || null;
  }, [currentUserId, teamUser]);

  useEffect(() => {
    if (Number(router.query.onboard) === 1) {
      setIsOpenCompleteDialog(true);
    }
  }, [router.query.onboard]);

  useEffect(() => {
    if (Number(router.query.reactivation) === 1) {
      setIsReactivation(true);
      setEvent(
        'trial reactivation completed',
        {
          teamId: props.viewer.team?.uid,
        },
        {
          isAmplitude: true,
        },
      );
    }
  }, [router.query.reactivation]);

  useEffect(() => {
    if (!router.isReady || typeof router.query.current !== 'string') return;

    const newDate = getDateTime(router.query.current, timeZone);
    setCurrentDate(newDate);
  }, [router.query.current]);

  useEffect(() => {
    if (isShowingTrialEnd) return;

    setIsShowingTrialEnd(
      props.viewer.role === Role.Owner &&
        props.viewer.trialChannel === TrialChannel.Jicoo &&
        props.viewer.trialEndedAt !== null &&
        new Date(props.viewer.trialEndedAt) <= new Date() &&
        props.viewer.initialPlanChoseAt === null,
    );
  }, []);

  useEffect(() => {
    if (!props.viewer) return;
    if (isAndroid()) {
      initializeWebPush(props.viewer);
    }
  }, [props.viewer]);

  const onChangeQueryKeyword = async (e: SyntheticEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;
    setQueryKeyword(value);
    setEvent('search event type', {
      keyword: value,
    });
  };

  const onChangePage = async (e: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const onDeleteEventType = async () => {
    if (!deleteEventType) return;
    setIsConfirmLoading(true);
    const result = await destroy(
      apiPath(
        `/v1/teams/${props.viewer.team!.uid}/event_types/${
          deleteEventType.id
        }/delete`,
      ),
    );
    if (!result.isSuccess) {
      setDeleteEventType(null);
      setIsConfirmLoading(false);
      dispatch(setErrorMessage(t('Failed Delete Event Type')));
      return;
    }

    const newEventTypes = eventTypes.filter((e) => e.id !== deleteEventType.id);
    eventTypesRequest.mutate(
      {
        ...eventTypesRequest,
        data: newEventTypes,
      },
      false,
    );
    setDeleteEventType(null);
    setIsConfirmLoading(false);

    if (subscriptionState) {
      const viewerEnabledEventTypeCount =
        subscriptionState.viewerEnabledEventTypeCount - 1;
      subscriptionStateRequest.mutate(
        {
          ...subscriptionStateRequest,
          data: {
            ...subscriptionState,
            viewerEnabledEventTypeCount,
          },
        },
        false,
      );
    }
  };

  const onClickNewEventType = async (e: SyntheticEvent<HTMLButtonElement>) => {
    if (canAddEventType) {
      router.push(hostEventTypeNewPath(projectUid));
    } else {
      setIsShowingPlanUpgrade(true);
    }
  };

  const onCopyEvent = async () => {
    if (!copyEventType) return;
    setIsConfirmLoading(true);
    let result = await post(
      apiPath(
        `/v1/teams/${props.viewer.team!.uid}/event_types/${
          copyEventType.id
        }/copy`,
      ),
    );
    if (!result.isSuccess) {
      setIsConfirmLoading(false);
      dispatch(setErrorMessage(t('Failed Copy Event Type')));
      return;
    }

    const createBody: EventTypeCreateResult = result.data;
    result = await get(
      apiPath(
        `/v1/teams/${props.viewer.team!.uid}/event_types/${createBody.id}`,
      ),
    );
    if (!result.isSuccess) {
      setIsConfirmLoading(false);
      dispatch(setErrorMessage(t('Failed Copy Event Type')));
      return;
    }
    const newEventType: EventTypeListItem = result.data;
    eventTypesRequest.mutate(
      {
        ...eventTypesRequest,
        data: [newEventType, ...eventTypes],
      },
      false,
    );
    setCopyEventType(null);
    setIsConfirmLoading(false);
  };

  const onCloseDialog = () => {
    setDeleteEventType(null);
    setCopyEventType(null);
  };

  const onChangeFilter = (filters: UserListFilter) => {
    const { userId, projectId } = filters;
    const user = props.viewer.uid === userId ? null : userId;
    setCurrentPage(1);
    router.push(dashboardPath({ user, project: projectId }));
  };

  const onClickShowProfile = async (
    eventType: EventTypeListItem,
    isShowProfile: boolean,
  ) => {
    const displayType = isShowProfile
      ? DisplayType.Public
      : DisplayType.TeamPublic;
    const data: EventTypeUpdateBody = {
      eventType: {
        displayType,
      },
    };
    const result = await patch(
      apiPath(
        `/v1/teams/${props.viewer.team!.uid}/event_types/${
          eventType.uid
        }/update`,
      ),
      { data },
    );

    if (!result.isSuccess) {
      dispatch(setErrorMessage(t('Failed Save Event Type')));
      return;
    }
    const newEventTypes = eventTypes.map((e) =>
      e.id === eventType.id
        ? {
            ...e,
            displayType,
          }
        : e,
    );
    eventTypesRequest.mutate(
      {
        ...eventTypesRequest,
        data: newEventTypes,
      },
      false,
    );
    setEvent('event type update display type', {
      uid: eventType.uid,
    });
  };

  const onSelectShareMenu = async (
    eventType: EventTypeListItem,
    selectedIndex: number,
  ) => {
    switch (selectedIndex) {
      case EventTypeShareOptionIndex.profile:
        break;
      case EventTypeShareOptionIndex.onetimeLink: {
        onClickOnetimeLink(eventType);
        break;
      }
      case EventTypeShareOptionIndex.addToWebsite: {
        setEmbedEventType(eventType);
        break;
      }
      case EventTypeShareOptionIndex.export: {
        const formExportFeature = new FormExportFeature({
          planVersion: props.viewer.planVersion,
        });
        if (formExportFeature.isValid(props.viewer.planId)) {
          if (isiOSApp) {
            const link = dashboardPath();
            if (
              typeof window.webkit?.messageHandlers?.requestToOpenBrowser
                ?.postMessage === 'function'
            ) {
              window.webkit.messageHandlers.requestToOpenBrowser.postMessage(
                webPath(
                  loginPath({ to: link, redirectIfLoggedIn: true }),
                  router.locale,
                ),
              );
            }
            return;
          }

          const result = await get(
            apiPath(`/v1/event_types/${eventType.uid}/export_csv`),
            {
              responseType: 'arraybuffer',
              headers: { Accept: 'application/zip' },
              timeout: 60 * 1000 * 10,
            },
          );
          if (!result.isSuccess) {
            dispatch(setErrorMessage(t('Failed Export Form CSV')));
            return;
          }
          if (result.data) {
            const data: ArrayBuffer = result.data;
            const url = window.URL.createObjectURL(
              new Blob([data], { type: 'application/zip' }),
            );
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${eventType.name}.zip`);
            document.body.appendChild(link);
            link.click();
            URL.revokeObjectURL(url);
            link.parentNode!.removeChild(link);
          }
        } else {
          setIsShowingPlanUpgrade(true);
        }
        break;
      }
      case EventTypeShareOptionIndex.share: {
        const url = webPath(
          eventTypePath({
            teamSlug: props.viewer.team!.slug,
            eventTypeSlug: eventType.slug,
          }),
          router.locale,
        );
        if (
          typeof window.webkit?.messageHandlers?.showShareSheet?.postMessage ===
          'function'
        ) {
          window.webkit.messageHandlers.showShareSheet.postMessage({
            text: eventType.name,
            url,
          });
        } else if (typeof window.navigator.share === 'function') {
          navigator.share({
            title: translate('siteName'),
            text: eventType.name,
            url,
          });
        }
        break;
      }
    }
  };

  const onSelectMenu = async (
    eventType: EventTypeListItem,
    selectedIndex: number,
  ) => {
    switch (selectedIndex) {
      case EventTypeOptionIndex.edit: {
        router.push(hostEventTypeEditPath(eventType.uid));
        break;
      }
      case EventTypeOptionIndex.copy: {
        if (canAddEventType) {
          setCopyEventType(eventType);
        } else {
          // 予約タイプ数がプラン上限ならダイアログ出す
          setIsShowingPlanUpgrade(true);
        }
        break;
      }
      case EventTypeOptionIndex.delete: {
        setDeleteEventType(eventType);
        break;
      }
      case EventTypeOptionIndex.activation: {
        if (!subscriptionState) return;

        // 無効から変えようとしている＆有効な予約タイプ数がプラン上限ならダイアログ出す
        if (eventType.status === Status.Disable && !canAddEventType) {
          setIsShowingPlanUpgrade(true);
          return;
        }

        // プラン内なら有効無効切り替え
        onSwitchActivation(eventType);
        break;
      }
      default:
        break;
    }
  };

  const onCloseAddToWebsite = () => {
    setEmbedEventType(null);
  };

  const onClosePlanUpgrade = () => {
    setIsShowingPlanUpgrade(false);
  };

  const onSwitchActivation = async (eventType: EventTypeListItem) => {
    const newStatus =
      eventType.status === Status.Enable ? Status.Disable : Status.Enable;
    const data: EventTypeStatusUpdateBody = {
      eventType: {
        status: newStatus,
      },
    };
    const result = await patch(
      apiPath(
        `/v1/teams/${props.viewer.team!.uid}/event_types/${
          eventType.uid
        }/update`,
      ),
      { data },
    );

    if (!result.isSuccess) {
      if (
        result.errors &&
        result.errors.status &&
        result.errors.status.length > 0
      ) {
        setActivateEventType(eventType);
        return;
      }
      dispatch(setErrorMessage(t('Failed Save Event Type')));
      return;
    }
    const newEventTypes = eventTypes.map((e) =>
      e.id === eventType.id
        ? {
            ...e,
            status: newStatus,
          }
        : e,
    );
    eventTypesRequest.mutate(
      {
        ...eventTypesRequest,
        data: newEventTypes,
      },
      false,
    );
    subscriptionStateRequest.mutate(
      {
        ...subscriptionStateRequest,
        data: {
          ...subscriptionState,
          viewerEnabledEventTypeCount: newEventTypes.filter(
            (e) => e.status === Status.Enable,
          ).length,
        },
      },
      false,
    );
  };

  const onClickSettings = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    router.push(hostEventTypeSettingPath());
  };

  const onCloseEventTypeActivate = () => {
    setActivateEventType(null);
  };

  const onClickForceActivateEventType = async (
    eventType: EventTypeListItem,
  ) => {
    const result = await patch(
      apiPath(
        `/v1/teams/${eventType.teamId}/event_types/${eventType.uid}/force_activate`,
      ),
    );
    if (!result.isSuccess) {
      dispatch(setErrorMessage(t('Failed Save Event Type')));
      return;
    }
    const newEventTypes = eventTypes.map((e) =>
      e.id === eventType.id
        ? {
            ...e,
            status: Status.Enable,
          }
        : e,
    );
    eventTypesRequest.mutate(
      {
        ...eventTypesRequest,
        data: newEventTypes,
      },
      false,
    );
  };

  const onCloseTrialEnd = async () => {
    setIsShowingTrialEnd(false);

    // 閉じたら次回出ないように、裏で初期プラン選択日時更新APIを叩いておく
    const data: SubscriptionChoosePlanBody = {
      initialPlanChoseAt: Date.now(),
    };
    await patch(
      apiPath(
        `/v1/admin/teams/${props.viewer.team!.uid}/subscription/choose_plan`,
      ),
      { data },
    );
  };

  function onStartTour() {
    setIsOpenTour(true);
    setEvent('open tour', {
      category: 'onboard',
      label: 'dashboard',
    });
  }

  function showEventType() {
    setEvent('click onboard complete event type');
    setIsOpenCompleteDialog(false);
  }

  function startReactivate() {
    setIsReactivation(false);
  }

  function showProductTour() {
    setEvent('click onboard complete event type');
    setIsOpenCompleteDialog(false);
    onStartTour();
  }

  function onCloseTourDialog() {
    setIsOpenTour(false);
  }

  function onCloseCompleteDialog() {
    setIsOpenCompleteDialog(false);
    const tourId = process.env['INTERCOM_PRODUCT_TOUR_ID'];
    setEvent('close tour', {
      category: 'onboard',
      label: 'dashboard',
      value: tourId,
    });
  }

  function onCloseReactivationDialog() {
    setIsReactivation(false);
  }

  const onClickEvent = (
    mergedEvent: IntegrationMergedEventListItem,
    userId: string,
  ) => {
    openMergedEvent(mergedEvent, userId, router);
  };

  const onSelectDate = (start: Date, end: Date) => {
    router.push(
      dashboardCalendarsPath({
        from: router.asPath,
        start: start.getTime(),
        end: end.getTime(),
        eventUser: currentUserId ?? props.viewer.uid,
      }),
    );
  };

  const handleChangeTerm = (action: DateControllerActionType) => {
    switch (action) {
      case 'prev': {
        const newDate = currentDate.minus({ days: 1 });
        router.replace(
          dashboardPath({
            current: formatToYMD(newDate, timeZone),
            user: currentUserId,
          }),
        );
        break;
      }
      case 'today': {
        router.replace(
          dashboardPath({
            current: formatToYMD(today, timeZone),
            user: currentUserId,
          }),
        );
        break;
      }
      case 'next': {
        const newDate = currentDate.plus({ days: 1 });
        router.replace(
          dashboardPath({
            current: formatToYMD(newDate, timeZone),
            user: currentUserId,
          }),
        );
        break;
      }
      default:
        break;
    }
  };

  const onClickOnetimeLink = async (eventType: EventTypeListItem) => {
    setEventTypeForOnetimeLink(eventType);
  };

  const onCloseOnetimeLinkDialog = () => {
    setEventTypeForOnetimeLink(null);
  };

  const onClickOnboard = () => {
    setIsOpenOnboard(!isOpenOnboard);
    setPersistentItemWithViewer(
      'isOpenEventTypeOnboard',
      !isOpenOnboard,
      infiniteCacheExpiration,
      props.viewer,
    );
  };

  const guideItems = [
    {
      querySelector: '.tour-target-eventType-item',
      title: t('GuideTour_1.0'),
      content: t('GuideTour_1.1'),
    },
    {
      querySelector: '.tour-target-eventType-item-copy-link',
      title: t('GuideTour_2.0'),
      content: t('GuideTour_2.1'),
    },
    {
      querySelector: '.tour-target-eventType-item-setting',
      title: t('GuideTour_3.0'),
      content: t('GuideTour_3.1'),
    },
    {
      querySelector: '.tour-target-setting',
      title: t('GuideTour_4.0'),
      content: t('GuideTour_4.1'),
    },
    {
      querySelector: '.tour-target-help',
      title: t('GuideTour_6.0'),
      content: t('GuideTour_6.1'),
    },
  ];

  return {
    state: {
      eventTypes,
      currentTeamUser,
      isLoading: eventTypesRequest.isLoading,
      isTodayLoading: userProfileRequest.isLoading,
      currentPage,
      numberOfPages: eventTypesRequest.numberOfPages,
      canAddEventType,
      isConfirmLoading,
      deleteEventType,
      copyEventType,
      embedEventType,
      isShowingPlanUpgrade,
      activateEventType,
      currentUserId,
      project,
      projectUid,
      isShowingTrialEnd,
      isOpenTour,
      isOpenCompleteDialog,
      isEventIntegrating,
      dateString,
      isToday,
      isReactivation,
      eventTypeForOnetimeLink,
      taskList: taskList.state,
      isOpenOnboard: isCompleteOnboard ? false : isOpenOnboard,
      completeTaskCount,
      allTaskCount,
      isFuture,
      date: currentDate,
      today,
      timeZone,
      startedAt,
      endedAt,
      events,
      isEventLoading,
      queryKeyword,
      guideItems,
    },
    actions: {
      onChangePage,
      onChangeFilter,
      onSelectMenu,
      onSelectShareMenu,
      onClickShowProfile,
      onDeleteEventType,
      onCloseDialog,
      onCopyEvent,
      onClickNewEventType,
      onCloseAddToWebsite,
      onClosePlanUpgrade,
      onClickSettings,
      onCloseEventTypeActivate,
      onClickForceActivateEventType,
      onCloseTrialEnd,
      onStartTour,
      onCloseTourDialog,
      onCloseCompleteDialog,
      onCloseReactivationDialog,
      showEventType,
      startReactivate,
      showProductTour,
      setIsEventIntegrating,
      onClickEvent,
      onSelectDate,
      handleChangeTerm,
      onCloseOnetimeLinkDialog,
      taskList: taskList.actions,
      onClickOnboard,
      onChangeQueryKeyword,
    },
  };
}
